import { create } from 'zustand';
import type { CanvasShape } from '../types';

export const MIN_CANVAS_SCALE = 0.1;
export const MAX_CANVAS_SCALE = 8;
export const ZOOM_STEP = 1.25;

interface CanvasState {
  shapes: CanvasShape[];
  scale: number;
  panX: number;
  panY: number;
  viewport: { width: number; height: number };
  history: unknown[];
  historyIndex: number;
  addShape: (shape: CanvasShape) => void;
  updateShape: (id: string, updates: Partial<CanvasShape>) => void;
  removeShape: (id: string) => void;
  setShapes: (shapes: CanvasShape[]) => void;
  setScale: (s: number) => void;
  setPan: (x: number, y: number) => void;
  setViewport: (width: number, height: number) => void;
  zoomBy: (factor: number) => void;
  pushHistory: () => void;
  undo: () => void;
  redo: () => void;
}

const defaultShapes: CanvasShape[] = [];

export const useCanvasStore = create<CanvasState>((set, get) => ({
  shapes: defaultShapes,
  scale: 1,
  panX: 0,
  panY: 0,
  viewport: { width: 1200, height: 800 },
  history: [JSON.stringify(defaultShapes)],
  historyIndex: 0,
  addShape: (shape) =>
    set((s) => {
      const next = [...s.shapes, shape];
      return { shapes: next };
    }),
  updateShape: (id, updates) =>
    set((s) => ({
      shapes: s.shapes.map((sh) => (sh.id === id ? { ...sh, ...updates } : sh)),
    })),
  removeShape: (id) =>
    set((s) => ({
      shapes: s.shapes.filter((sh) => sh.id !== id),
    })),
  setShapes: (shapes) =>
    set({
      shapes,
      history: [JSON.stringify(shapes)],
      historyIndex: 0,
    }),
  setScale: (scale) => set({ scale: Math.min(MAX_CANVAS_SCALE, Math.max(MIN_CANVAS_SCALE, scale)) }),
  setPan: (panX, panY) => set({ panX, panY }),
  setViewport: (width, height) => set({ viewport: { width, height } }),
  zoomBy: (factor) => {
    const { scale, panX, panY, viewport } = get();
    const next = Math.min(MAX_CANVAS_SCALE, Math.max(MIN_CANVAS_SCALE, scale * factor));
    if (next === scale) return;
    const cx = viewport.width / 2;
    const cy = viewport.height / 2;
    const canvasX = (cx - panX) / scale;
    const canvasY = (cy - panY) / scale;
    set({
      scale: next,
      panX: cx - canvasX * next,
      panY: cy - canvasY * next,
    });
  },
  pushHistory: () => {
    const { shapes } = get();
    set((s) => ({
      history: s.history.slice(0, s.historyIndex + 1).concat(JSON.stringify(shapes)),
      historyIndex: s.historyIndex + 1,
    }));
  },
  undo: () => {
    const { history, historyIndex } = get();
    if (historyIndex <= 0) return;
    const prev = JSON.parse(history[historyIndex - 1] as string) as CanvasShape[];
    set({ shapes: prev, historyIndex: historyIndex - 1 });
  },
  redo: () => {
    const { history, historyIndex } = get();
    if (historyIndex >= history.length - 1) return;
    const next = JSON.parse(history[historyIndex + 1] as string) as CanvasShape[];
    set({ shapes: next, historyIndex: historyIndex + 1 });
  },
}));
